import * as Sentry from '@sentry/react';
import {
  getV1CompaniesCompanyIdJobAssignmentsJobAssignmentIdAutoAddOnAssignments,
  getV1CompaniesCompanyIdJobsJobIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';

import { AddOnAssignment } from '~hooks/useAddOns/models';
import { Job } from '~hooks/useJob/models';

/**
 * Fetch the parent add-on assignments a job inherits for a given rate owner
 * (driver / vendor / customer).
 *
 * Driver add-ons hang off the Job itself; vendor and customer add-ons hang off
 * their respective JobAssignment. A job side that has not been assigned yet has
 * no JobAssignment, and therefore no parent add-ons to inherit. In that case we
 * must NOT call the API: an empty `job-assignment-id` builds the URL
 * `/v1/job_assignments//auto_add_on_assignments`, which always 404s and floods
 * the HORIZON-FE-1RY Sentry bucket.
 */
export async function fetchParentAddOnAssignmentsForJob(
  companyId: string,
  rateOwnerType: RateOwnerType,
  job?: Job,
  signal?: AbortSignal,
) {
  if (!job?.id) {
    return [];
  }

  const jobAssignmentId =
    rateOwnerType === RateOwnerType.VENDOR
      ? job.vendorJobAssignment?.id
      : job.customerJobAssignment?.id;

  try {
    if (rateOwnerType === RateOwnerType.DRIVER) {
      const response = await getV1CompaniesCompanyIdJobsJobIdAutoAddOnAssignments({
        path: { 'company-id': companyId, 'job-id': job.id },
        signal,
      });

      return response.data.data.map((addOnAssignment) =>
        AddOnAssignment.parse(addOnAssignment),
      );
    }

    if (!jobAssignmentId) {
      return [];
    }

    const response =
      await getV1CompaniesCompanyIdJobAssignmentsJobAssignmentIdAutoAddOnAssignments({
        path: { 'company-id': companyId, 'job-assignment-id': jobAssignmentId },
        signal,
      });

    return response.data.data.map((addOnAssignment) =>
      AddOnAssignment.parse(addOnAssignment),
    );
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }

    // Reported without a toast so the dialog still opens with the add-ons it could load.
    Sentry.captureException(error, {
      extra: { companyId, jobAssignmentId, jobId: job.id, rateOwnerType },
    });

    return [];
  }
}
